import { HTMLAttributes, RefObject, useEffect, useMemo, useState } from 'react';
import { useFocusWithin, useInteractOutside } from 'react-aria';
import { KeyboardEvent } from '@react-types/shared';
import { findLastIndex } from '@direct-frontend/stdlib';

import { DEFAULT_ROOT_LAYER_ID, layerManagerIgnoreOnDismissClassName } from '../../lib/layerManager/layerManager';

function getLastArrayElement<T>(array?: T[]): T | undefined {
    if (!array) {
        return undefined;
    }

    return array[array.length - 1];
}

// Взято из https://github.com/adobe/react-spectrum/blob/0a284c04343ed170f5410e9188dd722657072222/packages/@react-aria/overlays/src/useOverlay.ts
// Добавлена проверка на isModal
// Изменена логика стека открытых модалок

type OverlayProps = {
    /** Whether the overlay is currently open. */
    isOpen?: boolean;

    /** Handler that is called when the overlay should close. */
    onClose?: () => void;

    /** Handler that is called when the overlay shouldn't close. */
    onCloseCanceled?: () => void;

    /**
     * Whether to block interaction outside the overlay.
     * @default false
     */
    isModal?: boolean;

    /**
     * Whether to close the overlay when the user interacts outside it.
     * @default false
     */
    isDismissable?: boolean;

    /**
     * Должен ли попап быть добавлен в менеджер слоев
     *
     * TODO DIRECT-243504 Сделать общее решение, работающее с isDismissable
     *
     * @deprecated
     * @default true
     */
    isAddedToLayerManager?: boolean;

    /** Whether the overlay should close when focus is lost or moves outside it. */
    shouldCloseOnBlur?: boolean;

    /**
     * Whether pressing the escape key to close the overlay should be disabled.
     * @default false
     */
    isKeyboardDismissDisabled?: boolean;

    /**
     * When user interacts with the argument element outside of the overlay ref,
     * return true if onClose should be called.  This gives you a chance to filter
     * out interaction with elements that should not dismiss the overlay.
     * By default, onClose will always be called on interaction outside the overlay ref.
     */
    shouldCloseOnInteractOutside?: (element: HTMLElement) => boolean;

    /**
     * zIndex на котором расположен элемент
     */
    zIndex: number;
} & (
    | {
          /**
           * true, если используется в компоненте, создающем слой (Overlay)
           */
          isLayer: true;

          /**
           * Уникальный идентификатор слоя либо контекстного попапа
           */
          id: string;

          /**
           * Уникальный идентификатор родительского слоя
           */
          parentLayerId?: null;
      }
    | {
          /**
           * true, если используется в компоненте, создающем слой (Overlay)
           */
          isLayer: false;

          /**
           * Уникальный идентификатор слоя либо контекстного попапа
           */
          id: string;

          /**
           * Уникальный идентификатор родительского слоя
           */
          parentLayerId: string;
      }
);

interface OverlayAria {
    /** Props to apply to the overlay container element. */
    overlayProps: HTMLAttributes<HTMLElement>;
    /** Props to apply to the underlay element, if any. */
    underlayProps: HTMLAttributes<HTMLElement>;
}

interface ContextPopupItem {
    elementRef: RefObject<HTMLElement>;
    parentLayerId: string;
    id: string;
    isAddedToLayerManager: boolean;
}

interface LayersStackItem {
    elementRef: RefObject<HTMLElement>;
    zIndex: number;
    id: string;
}

class PrioritizedLayersStack {
    private layersStack: LayersStackItem[] = [
        {
            elementRef: { current: null },
            id: DEFAULT_ROOT_LAYER_ID,
            zIndex: 0,
        },
    ];

    // Ключ - id слоя
    private contextPopups = new Map<string, ContextPopupItem[]>();
    private containedIds = new Set<string>([DEFAULT_ROOT_LAYER_ID]);

    private topMostItemIdSubscribers = new Set<(topMostItemId: string | undefined) => void>();

    subscribe(callback: (topMostItemId: string | undefined) => void) {
        this.topMostItemIdSubscribers.add(callback);

        callback(this.getTopMostItemId());
    }

    unsubscribe(callback: (topMostItemId: string | undefined) => void) {
        this.topMostItemIdSubscribers.delete(callback);
    }

    private emitUpdate() {
        const topMostItemId = this.getTopMostItemId();

        this.topMostItemIdSubscribers.forEach(subscriber => {
            subscriber(topMostItemId);
        });
    }

    pushLayer(layerItem: LayersStackItem) {
        if (this.containedIds.has(layerItem.id)) {
            return;
        }

        const indexToInsert = findLastIndex(this.layersStack, stackItem => layerItem.zIndex >= stackItem.zIndex) + 1;
        this.layersStack.splice(indexToInsert, 0, layerItem);
        this.containedIds.add(layerItem.id);

        this.emitUpdate();
    }

    removeLayerItem(layerItem: LayersStackItem) {
        this.contextPopups.delete(layerItem.id);
        this.layersStack = this.layersStack.filter(stackItem => stackItem.id !== layerItem.id);

        this.containedIds.delete(layerItem.id);

        this.emitUpdate();
    }

    pushContextPopupItem(contextPopupItem: ContextPopupItem) {
        if (this.containedIds.has(contextPopupItem.id)) {
            return;
        }

        const layerContextPopups = this.contextPopups.get(contextPopupItem.parentLayerId) || [];

        this.contextPopups.set(contextPopupItem.parentLayerId, [...layerContextPopups, contextPopupItem]);

        this.containedIds.add(contextPopupItem.id);

        this.emitUpdate();
    }

    removeContextPopupItem(contextPopupItem: ContextPopupItem) {
        const { parentLayerId } = contextPopupItem;

        const layerContextPopups = this.contextPopups.get(parentLayerId) || [];
        const contextPopupsWithRemovedItem = layerContextPopups.filter(
            layerContextPopup => layerContextPopup.id !== contextPopupItem.id,
        );

        if (contextPopupsWithRemovedItem.length) {
            this.contextPopups.set(parentLayerId, contextPopupsWithRemovedItem);
        } else {
            this.contextPopups.delete(parentLayerId);
        }

        this.containedIds.delete(contextPopupItem.id);

        this.emitUpdate();
    }

    getTopMostLayerId(): string | undefined {
        const topMostLayer = getLastArrayElement(this.layersStack);

        return topMostLayer?.id;
    }

    getTopMostPopupId(): string | undefined {
        const topMostLayerId = this.getTopMostLayerId();
        const topMostContextPopup = topMostLayerId
            ? getLastArrayElement(
                  this.contextPopups.get(topMostLayerId)?.filter(contextPopup => contextPopup.isAddedToLayerManager),
              )
            : undefined;
        return topMostContextPopup?.id;
    }

    getTopMostItemId(): string | undefined {
        return this.getTopMostPopupId() ?? this.getTopMostLayerId();
    }
}

const openedModals = new PrioritizedLayersStack();

interface UseIsTopMostItemProps {
    isOpen?: boolean;
    id: string;
}

/**
 * Хук возвращающий флаг наличия открытых попапов или оверлеев на странице
 * @returns признак наличия открытых попапов или оверлеев на страницу
 */
export const useIsPopupOrOverlayOpened = () => {
    const [isPopupOrOverlayOpened, setIsPopupOrOverlayOpened] = useState(
        () => openedModals.getTopMostItemId() !== DEFAULT_ROOT_LAYER_ID,
    );

    useEffect(() => {
        const callbackFn = () => {
            const topMostItemId = openedModals.getTopMostItemId();

            if (topMostItemId === DEFAULT_ROOT_LAYER_ID) {
                setIsPopupOrOverlayOpened(false);
            } else {
                setIsPopupOrOverlayOpened(true);
            }
        };

        openedModals.subscribe(callbackFn);

        return () => {
            openedModals.unsubscribe(callbackFn);
        };
    }, []);

    return isPopupOrOverlayOpened;
};

/**
 * Хук возвращающий флаг наличия открытых оверлеев на странице
 * @returns признак наличия открытых оверлеев на странице
 */
export const useIsOverlayOpened = () => {
    const [isOverlayOpened, setIsOverlayOpened] = useState(
        () => openedModals.getTopMostLayerId() !== DEFAULT_ROOT_LAYER_ID,
    );

    useEffect(() => {
        const callbackFn = () => setIsOverlayOpened(openedModals.getTopMostLayerId() !== DEFAULT_ROOT_LAYER_ID);

        openedModals.subscribe(callbackFn);

        return () => {
            openedModals.unsubscribe(callbackFn);
        };
    }, []);

    return isOverlayOpened;
};

export const useIsTopMostItem = ({ isOpen, id }: UseIsTopMostItemProps): boolean => {
    const [topMostItemId, setTopMostItemId] = useState<string | undefined>(() => openedModals.getTopMostItemId());

    useEffect(() => {
        if (isOpen) {
            openedModals.subscribe(setTopMostItemId);
        }

        return () => {
            openedModals.unsubscribe(setTopMostItemId);
        };
    }, [isOpen]);

    return topMostItemId !== undefined && topMostItemId === id;
};

export function useOverlay(props: OverlayProps, ref: RefObject<HTMLElement>): OverlayAria {
    const {
        onClose,
        onCloseCanceled,
        shouldCloseOnBlur,
        isOpen,
        isModal,
        isDismissable = false,
        isKeyboardDismissDisabled = false,
        isAddedToLayerManager = true,
        shouldCloseOnInteractOutside,
        zIndex,
    } = props;

    const contextPopupItem: ContextPopupItem | undefined = useMemo(() => {
        if (!props.isLayer) {
            return {
                elementRef: ref,
                zIndex,
                parentLayerId: props.parentLayerId,
                id: props.id,
                isAddedToLayerManager,
            };
        }

        return undefined;
    }, [isAddedToLayerManager, props.id, props.isLayer, props.parentLayerId, ref, zIndex]);

    const layerItem: LayersStackItem | undefined = useMemo(() => {
        if (props.isLayer) {
            return { elementRef: ref, zIndex, id: props.id };
        }
        return undefined;
    }, [props.id, props.isLayer, ref, zIndex]);

    const isTopMostItem = useIsTopMostItem({ isOpen, id: props.id });

    useEffect(() => {
        if (isOpen) {
            if (layerItem) {
                openedModals.pushLayer(layerItem);
            } else if (contextPopupItem) {
                openedModals.pushContextPopupItem(contextPopupItem);
            }
        }

        return () => {
            if (layerItem) {
                openedModals.removeLayerItem(layerItem);
            } else if (contextPopupItem) {
                openedModals.removeContextPopupItem(contextPopupItem);
            }
        };
    }, [contextPopupItem, isOpen, layerItem]);

    // Only hide the overlay when it is the topmost visible overlay in the stack.
    const handleClose = () => {
        if (isTopMostItem && onClose) {
            onClose();
        }
    };

    // Only dismiss hide the overlay when it is the topmost visible overlay in the stack.
    const handleCloseCanceled = () => {
        if (isTopMostItem && onCloseCanceled) {
            onCloseCanceled();
        }
    };

    const onInteractOutsideStart = (e: PointerEvent) => {
        const shouldIgnoreOnDismiss = Boolean(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- необходимо для вызова closest
            (e.target as HTMLElement | null)?.closest(`.${layerManagerIgnoreOnDismissClassName}`),
        );

        if (shouldIgnoreOnDismiss) {
            return;
        }

        const isClosableOnInteractOutside =
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            !shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.target as HTMLElement);

        if (isDismissable && isClosableOnInteractOutside) {
            if (isTopMostItem && isModal) {
                e.stopPropagation();
                e.preventDefault();
            }

            handleClose();
        } else {
            handleCloseCanceled();
        }
    };

    const onInteractOutside = (e: PointerEvent) => {
        const shouldIgnoreOnDismiss = Boolean(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- необходимо для вызова closest
            (e.target as HTMLElement | null)?.closest(`.${layerManagerIgnoreOnDismissClassName}`),
        );

        if (shouldIgnoreOnDismiss) {
            return;
        }

        const isClosableOnInteractOutside =
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            !shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.target as HTMLElement);

        if (isDismissable && isClosableOnInteractOutside) {
            if (isTopMostItem && isModal) {
                e.stopPropagation();
                e.preventDefault();
            }
        }
    };

    // Handle the escape key
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            if (isKeyboardDismissDisabled) {
                handleCloseCanceled();
            } else {
                e.stopPropagation();
                e.preventDefault();

                handleClose();
            }
        }
    };

    // Handle clicking outside the overlay to close it
    useInteractOutside({ ref, onInteractOutside, onInteractOutsideStart });

    const { focusWithinProps } = useFocusWithin({
        isDisabled: !shouldCloseOnBlur,
        onBlurWithin: e => {
            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            if (!shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.relatedTarget as HTMLElement)) {
                onClose?.();
            }
        },
    });

    const onPointerDownUnderlay = (e: React.PointerEvent<HTMLElement>) => {
        // fixes a firefox issue that starts text selection https://bugzilla.mozilla.org/show_bug.cgi?id=1675846
        if (e.target === e.currentTarget) {
            // Отмена pointerdown приводит к тому, что не вызывается mousedown (и в бруазерах, и в jsdom)
            // Проблема в том, что react-aria при определении поддержки Pointer Events опирается на
            // наличие класса PointerEvent, которого нет в jsdom. В результате в тестах
            // вместо pointerdown используется mousedown, а он не вызывается.
            // @see https://github.com/adobe/react-spectrum/blob/fd3f09c798da23bf2148ba11f65e8856f8af9de6/packages/%40react-aria/interactions/src/useInteractOutside.ts#L65
            if (typeof globalThis.PointerEvent === 'undefined') {
                return;
            }

            e.preventDefault();
        }
    };

    return {
        overlayProps: {
            onKeyDown,
            ...focusWithinProps,
        },
        underlayProps: {
            onPointerDown: onPointerDownUnderlay,
        },
    };
}
